import React, { forwardRef, useMemo } from 'react';

import {
  PaginatedSelect,
  ParseMultipleItems,
  ParseSingleItem,
} from 'modules/workflowNodeEditors/components/formElements/PaginatedSelect/PaginatedSelect';
import {
  CampaignSelectProps,
  getCampaignSelectFetchItemsVariables,
} from 'modules/workflowNodeEditors/components/formElements/CampaignSelect/CampaignSelect';
import {
  fetchCampaignsQuery,
  fetchCampaignQuery,
  parseCampaignsResponse,
  parseCampaignResponse,
  Campaign,
} from 'modules/workflowNodeEditors/components/formElements/CampaignSelect/CampaignSelect.queries';
import { FilterSegmentSelectInputOption } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { getFilterSegmentSelectInputTriggerText } from 'modules/journey/components/FilterInputsList/utils';

import { FilterCampaignOption } from './FilterCampaignOption';
import { FilterCampaignOptionObject } from './FilterCampaignInput.types';
import { FilterSegmentButton } from '../../FilterSegmentButton';
import { FilterSegmentErrorButton } from '../../FilterSegmentErrorButton';

const displayText = {
  placeholder: 'Select a Campaign',
};

type TriggerProps = {
  children?: React.ReactNode;
  placeholder: string;
  selectedOptions: FilterSegmentSelectInputOption[];
  toggleDropdown: () => void;
  isOpen: boolean;
  isError: boolean;
  isDisabled: boolean;
};

const Trigger = forwardRef<HTMLDivElement, TriggerProps>(
  ({ placeholder, selectedOptions, toggleDropdown, children, isError, isDisabled }, ref) => {
    const triggerText = useMemo(
      () =>
        getFilterSegmentSelectInputTriggerText(
          // if the option exists but doesnt have a label, get the label which is passed in as a child
          selectedOptions.length && !selectedOptions[0]?.labelInTriggerButton
            ? [{ ...selectedOptions[0], labelInTriggerButton: children }]
            : selectedOptions,
          placeholder,
        ),
      [selectedOptions, placeholder, children],
    );

    const ButtonComponent = isError ? FilterSegmentErrorButton : FilterSegmentButton;
    return (
      <div ref={ref}>
        <ButtonComponent onClick={toggleDropdown} isDisabled={isDisabled}>
          {triggerText}
        </ButtonComponent>
      </div>
    );
  },
);

export function FilterCampaignInput<TItem extends Campaign = Campaign>(
  props: CampaignSelectProps<TItem>,
) {
  const defaultVariables = getCampaignSelectFetchItemsVariables();

  return (
    <PaginatedSelect
      fetchAllItemsQuery={fetchCampaignsQuery}
      fetchAllItemsParser={parseCampaignsResponse as ParseMultipleItems<TItem>}
      fetchAllItemsVariables={defaultVariables}
      fetchSelectedItemQuery={fetchCampaignQuery}
      fetchSelectedItemParser={parseCampaignResponse as ParseSingleItem<TItem>}
      placeholder={displayText.placeholder}
      parseOption={({ id: optionId, name, messageMedium }: FilterCampaignOptionObject) => ({
        id: optionId,
        label: <FilterCampaignOption id={optionId} name={name} messageMedium={messageMedium} />,
        value: `${optionId}`,
        labelInTriggerButton: name,
      })}
      Trigger={Trigger as React.FunctionComponent<React.PropsWithChildren<any>>}
      {...props}
    />
  );
}
